import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from '../program-sheet.module.scss';
import clsx from 'clsx';

import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';
import Typography from '@smart-tv/components/typography/typography';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import { isBackKey, isDownKey, isUpKey } from '@smart-tv/utils/remoteKeys';

import { rootStore } from '../_stores/store-root';
import { uiStore } from '../_stores/store-ui';
import ProgramSheetContentCrew, { CrewMember } from './sheet-description-extended-crew';
import ProgramSheetContentProviders from './sheet-description-extended-providers';
import { testIds } from '@smart-tv/e2e/shared/variables';

interface ScrollableProps {
  description?: string;
  crew?: CrewMember[];
}

const DescriptionExtendedScrollable: React.FC<ScrollableProps> = (props) => {
  const { description, crew } = props;

  const [scrollable, setScrollable] = React.useState(false);

  const { ref, focused, focusSelf } = useFocusable({ focusable: scrollable });

  const handleArrowKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      focused && isDownKey(event)
        ? ref.current?.scrollTo({ top: ref.current.scrollTop + 100, behavior: 'smooth' })
        : focused && isUpKey(event)
        ? ref.current?.scrollTo({ top: ref.current.scrollTop - 100, behavior: 'smooth' })
        : null;
    },
    [focused, ref]
  );

  React.useEffect(() => {
    const element = ref.current;
    element && element.scrollHeight > element.clientHeight && setScrollable?.(true);
  }, [ref, setScrollable]);

  useEventListener('keydown', (event: KeyboardEvent) => handleArrowKeyPress(event));

  return (
    <div
      className={clsx(styles['sheet-description-extended__content'], {
        [styles['sheet-description-extended__content--focused']]: focused
      })}
      ref={ref}
      onFocus={focusSelf}
    >
      <Typography className={styles['sheet-description-extended__description']} variant="bodySmall">
        {description}
      </Typography>

      <ProgramSheetContentProviders />
      <ProgramSheetContentCrew variant="director" crew={crew} />
      <ProgramSheetContentCrew variant="actor" crew={crew} />
    </div>
  );
};

const SheetDescriptionExtended = observer(() => {
  const { sheet } = rootStore.sheetStore;
  const { focusedEpisode, episodes, nextEventForSeries } = rootStore.episodesStore;
  const { extendedEpisodeDescriptionDialog } = uiStore.toggles;
  const dataProvider = extendedEpisodeDescriptionDialog.dataProvider;

  const selectFocusedEpisode = focusedEpisode.isNextEventforSeries
    ? nextEventForSeries?.episodeId === focusedEpisode.episodeId
      ? nextEventForSeries
      : undefined
    : episodes.find(
        (episode) =>
          episode.episodeId === focusedEpisode.episodeId &&
          episode.isNextEventforSeries === focusedEpisode.isNextEventforSeries
      );

  type DisplayData = {
    title?: string;
    seasonNumber?: number;
    episodeNumber?: number;
    description?: string;
    crew?: any;
    directors?: any;
  };

  const displayData: DisplayData = React.useMemo(() => {
    if (dataProvider === 'episode') {
      return {
        title: selectFocusedEpisode?.episodeTitle,
        seasonNumber: selectFocusedEpisode?.seasonNumber,
        episodeNumber: selectFocusedEpisode?.episodeNumber,
        description: selectFocusedEpisode?.longDescription,
        crew: selectFocusedEpisode?.cast
      };
    }
    return {
      title: sheet.episodeTitle || sheet.title,
      seasonNumber: sheet.seasonNumber,
      episodeNumber: sheet.episodeNumber,
      description: sheet.description,
      crew: sheet.cast
    };
  }, [dataProvider, selectFocusedEpisode, sheet]);

  const { title, seasonNumber, episodeNumber } = displayData;
  const hasSeasonNumber = typeof seasonNumber === 'number' && seasonNumber > 0;
  const hasEpisodeNumber = typeof episodeNumber === 'number' && episodeNumber > 0;

  const handleCloseExtendedDescriptionView = React.useCallback(() => {
    uiStore.toggleExtendedEpisodeDescriptionDialog(false, dataProvider);
    if (dataProvider === 'episode') {
      uiStore.setFocusOnKey('focusBtnReadMore');
    }
  }, [dataProvider]);

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event)) {
        event.stopImmediatePropagation();
        handleCloseExtendedDescriptionView();
      }
    },
    [handleCloseExtendedDescriptionView]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  if (!displayData) return null;

  return (
    <div className={styles['sheet-description-extended']}>
      <Typography variant="body" fontWeight="bold">
        {sheet.title}
        {sheet.title && (hasSeasonNumber || hasEpisodeNumber) && ' . '}
        {hasSeasonNumber && `Sæson ${seasonNumber}`}
        {hasSeasonNumber && hasEpisodeNumber && ' . '}
        {hasEpisodeNumber && `Episode ${episodeNumber}`}
      </Typography>
      <Typography
        className={styles['sheet-description-extended__title']}
        variant="h2"
        fontWeight="regular"
        data-testid={testIds.programsheet.content.extendedDescription.title}
      >
        {title}
      </Typography>
      <div className={styles['sheet-description-extended__container']}>
        <Button
          isFocused={true}
          onClick={handleCloseExtendedDescriptionView}
          className={styles['sheet-description-extended__back-button']}
          data-testid={testIds.programsheet.content.extendedDescription.backButton}
        >
          Tilbage
        </Button>

        <DescriptionExtendedScrollable {...displayData} />
      </div>
    </div>
  );
});

SheetDescriptionExtended.displayName = 'SheetDescriptionExtended';

export default SheetDescriptionExtended;
